import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { NotificationChannel } from "../../proto/generated/common";
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { selectMultiPaneFullScreen } from "../../selectors/common";
import { useDispatch, useSelector } from "react-redux";
import { LOGOS_BASE_URL, MULTIPANE_FULLSCREEN_MODE_ID } from "../../assets/constants";
import AlertSettingsTab from "./alertSettingsTab/alertSettingsTab";
import {addUserAlert, AlertModalPurpose, removeAlertModalData, updateUserAlert} from "../../reducer/alerts.reducer";
import {selectAlertModalData} from "../../selectors/alerts";
import {createAlert} from "../../services/apis/alerts";
import { getCreateUserAlertRequestObject, hasUsableTopLevelAlertSecurity } from "../../util/alerts";
import { selectIndicators } from "../../selectors/chart";
import { getSecurityAsync, getSecurityKey } from "../../util/security";
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { useNonModalDialog } from '../common/useNonModalDialog'
import ClearIcon from "../../assets/icons/ClearIcon";
import {ExtraSmallButton} from "../custom";
import ArrowBackIcon from "../../assets/icons/ArrowBackIcon";
import type { AlertSettingsSubView } from "./alertModal.typings";
import { clearConditionalEntryAlertModalHandlers, invokeConditionalEntryApply, invokeConditionalEntryCancel } from "../../util/conditionalEntryAlertModalBridge";
import { isConditionalEntryReady, rewriteConditionalLhsSecurityForUnderlyingChartRef } from "../../util/conditionalEntry";

function conditionalRuleModalTitle(purpose: AlertModalPurpose): string {
    switch (purpose) {
        case 'conditionalExit':
            return 'CONDITIONAL EXIT'
        case 'conditionalModify':
            return 'MODIFY CONDITION'
        case 'conditionalEntry':
            return 'CONDITIONAL ENTRY'
    }
}

export const alertModalMenuProps = { sx: { zIndex: 15000 } }

const AlertModal: React.FC = () => {

    const dispatch = useDispatch();
    const container = useSelector(selectMultiPaneFullScreen)
    const indicators = useSelector(selectIndicators)
    const { chartSnapshot, alert, edit, purpose, conditionalModalChartRef, conditionalModalChartVariants } =
        useSelector(selectAlertModalData) ?? {}
    const isConditionalRuleModal =
        purpose === 'conditionalEntry' ||
        purpose === 'conditionalExit' ||
        purpose === 'conditionalModify'
    const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([NotificationChannel.DESKTOP_POPUP])
    const [activeTab, setActiveTab] = useState('settings')
    const [settingsSubView, setSettingsSubView] = useState<AlertSettingsSubView>(null)
    const [error, setError] = useState(false)
    const [dialogPaperMinHeight, setDialogPaperMinHeight] = useState<number | undefined>(undefined)
    const [conditionSubViewActions, setConditionSubViewActions] = useState<{
        save: () => void
        saveLabel: string
    } | null>(null)
    const [resolvedHeaderSecurity, setResolvedHeaderSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)

    const headerSecurityKey = useMemo(() => {
        const top = alert?.security
        if (hasUsableTopLevelAlertSecurity(top)) {
            return getSecurityKey(top!.exchange, top!.code)
        }
        const s0 = chartSnapshot?.securityList?.[0]
        if (s0) return getSecurityKey(s0.exchange, s0.code)
        return ''
    }, [alert?.security, chartSnapshot?.securityList])

    useEffect(() => {
        if (!headerSecurityKey) {
            setResolvedHeaderSecurity(undefined)
            return
        }
        let cancelled = false
        getSecurityAsync(headerSecurityKey).then((s) => {
            if (!cancelled) setResolvedHeaderSecurity(s)
        })
        return () => {
            cancelled = true
        }
    }, [headerSecurityKey])

    const primarySymbolDisplayName = useMemo(() => {
        const r = resolvedHeaderSecurity
        if (r) return r.symbol || r.displayName || r.code || ''
        const top = alert?.security
        if (hasUsableTopLevelAlertSecurity(top)) {
            return top!.code || ''
        }
        const primarySecurity = chartSnapshot?.securityList?.[0] as any
        if (!primarySecurity) return ''
        return primarySecurity.symbol || primarySecurity.displayName || primarySecurity.code || ''
    }, [resolvedHeaderSecurity, alert?.security, chartSnapshot])

    const primarySecurityLogo = useMemo(() => {
        const logoUrl = resolvedHeaderSecurity?.logoUrl
        if (!logoUrl) return ''
        return `${LOGOS_BASE_URL}${logoUrl}.svg`
    }, [resolvedHeaderSecurity])

    const handleTabChange = (_event: React.ChangeEvent<{}>, newTab: string) => {
        setActiveTab(newTab)
        if (newTab !== 'settings') {
            setSettingsSubView(null)
        }
    }

    const settingsSubViewTitle = useMemo(() => {
        if (!settingsSubView) return null
        if (settingsSubView.kind === 'editCondition') return 'Edit condition'
        return settingsSubView.childPath === null ? 'Add condition' : 'Add nested condition'
    }, [settingsSubView])

    const exitSettingsSubView = useCallback(() => setSettingsSubView(null), [])

    const closeModal = useCallback(() => {
        if (
            purpose === 'conditionalEntry' ||
            purpose === 'conditionalExit' ||
            purpose === 'conditionalModify'
        ) {
            invokeConditionalEntryCancel()
        }
        clearConditionalEntryAlertModalHandlers()
        dispatch(removeAlertModalData())
    }, [dispatch, purpose])

    const handleEscapeKey = useCallback(() => {
        if (settingsSubView) {
            exitSettingsSubView()
        } else {
            closeModal()
        }
    }, [settingsSubView, exitSettingsSubView, closeModal])

    const createUserAlert = useCallback(async () => {
        if (!alert) return;
        const newAlert = await createAlert(
            getCreateUserAlertRequestObject(alert, {
                chartSnapshot,
                indicators,
            })
        )
        if (edit) {
            dispatch(updateUserAlert(newAlert))
        } else {
            dispatch(addUserAlert(newAlert))
        }
        closeModal();
    }, [alert, chartSnapshot, edit, closeModal, dispatch, indicators])

    const handleConditionalApply = useCallback(() => {
        const draftAlert = alert
        if (!draftAlert?.condition) return
        const condition = rewriteConditionalLhsSecurityForUnderlyingChartRef(
            draftAlert.condition,
            conditionalModalChartRef,
            conditionalModalChartVariants
        )
        if (!isConditionalEntryReady({ enabled: true, condition })) return
        invokeConditionalEntryApply(condition)
        clearConditionalEntryAlertModalHandlers()
        dispatch(removeAlertModalData())
    }, [dispatch, alert, conditionalModalChartRef, conditionalModalChartVariants])

    const onValidation = useCallback((error: boolean) => setError(error), [])

    const { dialogProps, paperRef } = useNonModalDialog(Boolean(alert), closeModal, {
        onEscape: handleEscapeKey
    })

    const captureDialogPaperHeight = useCallback(() => {
        const el = paperRef.current
        if (!el) return
        const h = Math.ceil(el.getBoundingClientRect().height)
        if (h > 0) setDialogPaperMinHeight(h)
    }, [paperRef])

    const handleSettingsSubViewChange = useCallback((next: AlertSettingsSubView | null) => {
        if (next) {
            captureDialogPaperHeight()
        }
        setSettingsSubView(next)
    }, [captureDialogPaperHeight])

    useLayoutEffect(() => {
        if (settingsSubView || !alert) return
        captureDialogPaperHeight()
    }, [alert, settingsSubView, activeTab, captureDialogPaperHeight])

    useEffect(() => {
        if (settingsSubView) return
        const el = paperRef.current
        if (!el) return
        const update = () => {
            const h = Math.ceil(el.getBoundingClientRect().height)
            if (h > 0) setDialogPaperMinHeight(h)
        }
        update()
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [settingsSubView, activeTab, paperRef])

    const primaryDisabled = useMemo(() => {
        if (error) return true
        if (!isConditionalRuleModal || !alert?.condition) return false
        return !isConditionalEntryReady({ enabled: true, condition: alert.condition })
    }, [error, isConditionalRuleModal, alert?.condition])

    if (!alert) {
        return null
    }

    return <Dialog
        open
        onClose={closeModal}
        fullWidth
        maxWidth="xs"
        container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
        {...dialogProps}
        sx={{
            ...(typeof dialogProps.sx === 'object' && dialogProps.sx !== null ? dialogProps.sx : {}),
            // Above AddQuickTrade (13000) and other nested quick-trade dialogs.
            ...(isConditionalRuleModal ? { zIndex: 14000 } : {}),
        }}
        PaperProps={{
            ref: paperRef,
            sx: {
                pointerEvents: 'auto',
                ...(dialogPaperMinHeight ? { minHeight: dialogPaperMinHeight } : {})
            }
        }}
        aria-labelledby="alerts-dialog">
        <DraggableDialogTitle>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                {settingsSubView && activeTab === 'settings' ? (
                    <>
                        <Stack direction="row" alignItems="center" spacing={0.75} minWidth={0} flex={1}>
                            <IconButton
                                size="small"
                                onClick={exitSettingsSubView}
                                onMouseDown={(e) => e.stopPropagation()}
                                aria-label="Back to alert settings"
                                color="inherit"
                            >
                                <ArrowBackIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {settingsSubViewTitle}
                            </Typography>
                        </Stack>
                        <ExtraSmallButton onClick={closeModal} aria-label="close" color={"inherit"}>
                            <ClearIcon/>
                        </ExtraSmallButton>
                    </>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1.2} minWidth={0}>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {isConditionalRuleModal && purpose
                                    ? conditionalRuleModalTitle(purpose)
                                    : edit
                                      ? 'Edit alert on'
                                      : 'Create alert on'}
                            </Typography>
                            <Avatar
                                src={primarySecurityLogo || undefined}
                                sx={{ width: 18, height: 18, fontSize: 14 }}
                            >
                                {(primarySymbolDisplayName || 'S').charAt(0)}
                            </Avatar>
                            <Typography fontWeight={500} fontSize={16} noWrap>
                                {primarySymbolDisplayName}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <ExtraSmallButton onClick={closeModal} aria-label="close" color={"inherit"}>
                                <ClearIcon/>
                            </ExtraSmallButton>
                        </Stack>
                    </>
                )}
            </Stack>
        </DraggableDialogTitle>
        <DialogContent>
            {notificationChannels.length > 1 && !settingsSubView ?
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="inherit"
                    >
                        <Tab label="Settings" value="settings"/>
                        <Tab label="Notifications" value="notifications"/>
                    </Tabs>
                </Box>
                :
                <></>
            }
            {
                activeTab === "settings" && (
                    <AlertSettingsTab
                        onValidation={onValidation}
                        settingsSubView={settingsSubView}
                        onSettingsSubViewChange={handleSettingsSubViewChange}
                        onConditionSubViewActions={setConditionSubViewActions}
                    />
                )
            }
        </DialogContent>
        <DialogActions>
            <Stack direction={'row'} alignItems={'right'} spacing={1}>
                {conditionSubViewActions ? (
                    <>
                        <Button variant="outlined" onClick={exitSettingsSubView}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={conditionSubViewActions.save}>
                            {conditionSubViewActions.saveLabel}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="outlined" onClick={closeModal}>
                            Cancel
                        </Button>
                        <Button autoFocus
                                disabled={primaryDisabled}
                                variant={'contained'}
                                data-event={isConditionalRuleModal ? 'Conditional entry condition applied' : `Alert ${edit ? 'Saved' : 'Created'}`}
                                data-event-alert-message={alert?.message}
                                data-event-alert-name={alert?.name}
                                onClick={isConditionalRuleModal ? handleConditionalApply : createUserAlert}
                        >
                            {isConditionalRuleModal ? 'Apply' : (edit ? 'Save' : 'Create')}
                        </Button>
                    </>
                )}
            </Stack>
        </DialogActions>
    </Dialog>
}
export default memo(AlertModal);